import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { format, eachDayOfInterval, startOfMonth, endOfMonth, eachMonthOfInterval } from "date-fns";
import { DefaultLayout } from "../../layouts/Default";
import { TopNavigation } from "../../themes/Default/components/TopNavigation";
import { Title } from "../../themes/Default/components/Title";
import { Breadcrumps } from "../../themes/Default/components/Breadcrumps";
import { Link } from "../../themes/Default/components/Breadcrumps/components/Link";
import { Main } from "../../themes/Default/components/Main";
import { Tabs } from "../../themes/Default/components/Tabs";
import { Wizard } from "../../themes/Default/components/Wizard";
import { Step } from "../../themes/Default/components/Wizard/Step";
import { ButtonBack } from "../../themes/Default/components/TopNavigation/components/Button";
import { Calendar } from "../../themes/Default/components/Calendar";
import { Month } from "../../themes/Default/components/Calendar/components/Month";
import { Day, DayOffset } from "../../themes/Default/components/Calendar/components/Month/components/Day";
import { groupsManagement } from "../../hooks/api/GroupsManagement";
import { useTranslation } from "react-i18next";
import { Slide, toast, ToastContainer } from "react-toastify";
import { calendarHolidaysManagement } from "../../hooks/api/CalendarHolidaysManagement";
import { Toaster } from "../../themes/Default/components/Toaster";

const AddGroupCalendar = () => {
    const { t } = useTranslation("common");
    const navigate = useNavigate();
    const [dates, setDates] = useState([]);
    const [totalSelectedDates, setTotalSelectedDates] = useState([]);
    const [classNumber, setClassNumber] = useState([]);
    const [startDate, setStartDate] = useState();
    const [endDate, setEndDate] = useState();
    const { groupId } = useParams();
    const [months, setMonths] = useState([]);
    const [groupName, setGroupName] = useState("");
    const [hasTests, setHasTests] = useState(false);
    const [hasProgress, setHasProgress] = useState(false);
    const [hasReport, setHasReport] = useState(false);
    const [hasPlan, setHasPlan] = useState(false);
    const [isStarted, setIsStarted] = useState([]);
    const { useAddGroupDatesMutation, useListGroupDatesMutation, useViewGroupDateMutation, useDeleteGroupDateMutation, useViewGroupMutation, useStartGroupMutation, useCanGroupStartMutation } = groupsManagement;

    const [addGroupDates] = useAddGroupDatesMutation();
    const [listGroupDates] = useListGroupDatesMutation();
    const [viewGroupDate] = useViewGroupDateMutation();
    const [deleteGroupDate] = useDeleteGroupDateMutation();
    const [viewGroup] = useViewGroupMutation();
    const [holidays, setHolidays] = useState();
    const { useCalendarHolidaysListMutation } = calendarHolidaysManagement;
    const [holidayList, { isLoading }] = useCalendarHolidaysListMutation();

    const [classStartTime, setClassStartTime] = useState([]);
    const [classEndTime ,setClassEndTime] = useState([]);

    const tabs = [
        ...(isStarted ? [
            { title: t('groups.attendance'), onClick: () => gotoAttendanceTab(groupId)  },
            ...(hasPlan ? [
                { title: t('groups.planning'), onClick: () => gotoPlanningTab(groupId)},
            ] : []), 
            ...(hasProgress ? [
                { title: t('groups.progress'), onClick: () => gotoProgressTab(groupId)},
            ] : []), 
            ...(hasTests ? [
                { title: t('groups.progressTest'), onClick: () => gotoProgressTestTab(groupId) }
            ] : []), 
            ...(hasReport ? [
                { title: t('reports.title'),onClick: () => gotoReportTab(groupId)},
            ] : []), 
            { title: t('documents'), onClick: () => gotoDocumentsTab(groupId)},
            { title: t('notes'), onClick: () => gotoNotesTab(groupId)},
        ] : []),
        { title: t("settings.title"), link: "/settings/reports", isActive: true },
    ];

    const gotoPlanningTab = (groupId) => {
        navigate("/groups/" + groupId + "/planning");
    }

    const gotoReportTab = (groupId) => {
        navigate("/groups/" + groupId + "/reports");
    }

    const gotoProgressTestTab = (groupId) => {
        navigate("/groups/" + groupId + "/tests");
    }

    const gotoProgressTab = (groupId) => {
        navigate("/groups/" + groupId + "/progress");
    }

    const gotoNotesTab = (groupId) => {
        navigate("/groups/" + groupId + "/notes");
    }

    const gotoDocumentsTab = (groupId) => {
        navigate("/groups/" + groupId + "/documents");
    }

    const gotoAttendanceTab = (groupId) => {
        navigate("/groups/" + groupId + "/attendance");
    }

    const gotoDetails = (values) => {
        navigate("/groups/edit/" + groupId);
    };

    const gotoStudentsStep = () => {
        navigate("/groups/" + groupId + "/students/add");
    };

    const gotoProfessorStep = () => {
        navigate("/groups/" + groupId + "/professor");
    };

    const onSave = (dateId, date) => {
        viewGroupDate({ dateId, groupId }).then((result) => {
            if (result?.data?.id) {
                toast.warn(
                    ({ closeToast }) => (
                        <Toaster
                            closeToast={closeToast}
                            description={t("areYouSure")}
                            optionNo={t("no")}
                            optionYes={t("yes")}
                            onClick={() => {
                                closeToast();
                                deleteGroupDate({ dateId, groupId }).then((result) => {
                                    if (result?.error) {
                                        toast.error(result?.error?.data?.error?.responseMessage);
                                    } else {
                                        toast.info(result?.data?.info?.responseMessage);
                                        loadMonths();
                                    }
                                });
                            }}
                        />
                    ),
                    {
                        autoClose: false,
                        progress: false,
                        closeButton: false,
                    },
                );
            } else {
                const body = {
                    dates: [
                        {
                            date: date,
                            timeFrom: classStartTime,
                            timeTo: classEndTime,
                        },
                    ],
                };
                addGroupDates({ body, groupId }).then((result) => {
                    if (result?.error) {
                        toast.error(result?.error?.data?.error?.responseMessage);
                    } else {
                        toast.info(result?.data?.info?.responseMessage);
                        loadMonths();
                    }
                });
            }
        });
    };

    const monthNames = [
        t("calendar.january"),
        t("calendar.february"),
        t("calendar.march"),
        t("calendar.april"),
        t("calendar.may"),
        t("calendar.june"),
        t("calendar.july"),
        t("calendar.august"),
        t("calendar.september"),
        t("calendar.october"),
        t("calendar.november"),
        t("calendar.december"),
    ];

    const isCancel = (dateId) => {
        return dates.some(date => date.id === dateId && date.isCancel === true);
    }

    const loadMonths = () => {
        listGroupDates(groupId).then((result) => {
            setDates(result?.data?.records);
            const months = eachMonthOfInterval({
                start: startOfMonth(startDate),
                end: endOfMonth(endDate),
            });
            setMonths(months);
            setTotalSelectedDates(result?.data?.totalRecords);
        });
    };

    const loadGroupName = () => {
        viewGroup(groupId).then((result) => {
            setGroupName(result?.data?.name);
            setHasTests(result?.data?.hasTests);
            setHasReport(result?.data?.hasReport);
            setHasProgress(result?.data?.hasProgress);
            setHasPlan(result?.data?.hasPlan);
            setClassNumber(result?.data?.classNumber);
            setStartDate(result?.data?.startDate ? new Date(result.data.startDate) : new Date("2025-01-01"));
            setEndDate(result?.data?.endDate ? new Date(result.data.endDate) : new Date("2025-12-31"));
            setIsStarted(result?.data?.hasStarted);

            setClassStartTime(result?.data?.classStartTime);
            
            const [hours, minutes] = result?.data?.classStartTime.split(':').map(Number);
            const now = new Date();
            now.setHours(hours, minutes, 0, 0); 

            const endDate = new Date(now.getTime() + result?.data?.classDuration * 60000);

            const endHours = endDate.getHours().toString().padStart(2, '0');
            const endMinutes = endDate.getMinutes().toString().padStart(2, '0');
            const endTime = `${endHours}:${endMinutes}`;
            setClassEndTime(endTime);
        });
    };

    const checkHoliday = (selectedDate) => {
        const holiday = holidays.find((holiday) => holiday.date === selectedDate);
    
        if (holiday) {
            toast.error(
                <>
                    Izabrali ste praznik <br />
                    {holiday.title}
                </>
            );
            return true;
        }
    
        return false;
    };

    useEffect(() => {
        loadGroupName();
    }, []);

    useEffect(() => {
        if (startDate) {
            loadMonths();
        }
    }, [startDate]);

    useEffect(() => {
        holidayList().then((result) => {
            const holidays = result?.data?.records;
            setHolidays(holidays);
        });
    }, [holidayList]);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to="/groups" />
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Title fullWidth={"100%"}>
                    {groupName}
                    <span className="numOfClasses">
                        {t("groups.classNumber")}:{" "}
                        <span className="numOfClassesFrstNum" style={totalSelectedDates !== classNumber ? { color: "#EB5757" } : {}}>
                            {totalSelectedDates}
                        </span>
                        <span className="numOfClassesScndNum">/{classNumber}</span>
                    </span>
                </Title>
                <Breadcrumps>
                    <Link title={t("groups.title")} icon="fa-light fa-users" />
                    <Link title={t("details")} isActive={true} />
                </Breadcrumps>
                <br />
                <br />
                <br />
                <Wizard>
                    <Step title={t("groups.basicInfo")} isChecked={true} onClick={gotoDetails} />
                    <Step title={t("groups.chooseStudents")} isChecked={true} onClick={gotoStudentsStep} />
                    <Step title={t("groups.chooseProfessor")} isChecked={true} onClick={gotoProfessorStep} />
                    <Step title={t("groups.selectDates")} isActive={true} />
                </Wizard>
                <br />
                <br />
                <br />
                <Calendar>
                    {months.map((month, index) => {
                        const days = eachDayOfInterval({
                            start: startOfMonth(month),
                            end: endOfMonth(month),
                        });

                        return (
                            <Month month={monthNames[month.getMonth()]} year={month.getFullYear()} key={index}>
                                <DayOffset days={days} />
                                {days.map((day, index) => {
                                    let isWeekend = day.getDay() === 6 || day.getDay() === 0;
                                    let isSelected = dates.some((item) => item.date === format(day, "yyyy-MM-dd"));

                                    const selectedItem = dates.find((item) => item.date === format(day, "yyyy-MM-dd"));
                                    const dateId = selectedItem ? selectedItem.id : 0;
                                    const isCanceled = isCancel(dateId);
                                    return (
                                        <Day
                                            key={index}
                                            isWeekend={isWeekend}
                                            isSelected={isSelected}
                                            isCanceled={isCanceled}
                                            value={format(day, "d")}
                                            onClick={() => {
                                                if (!checkHoliday(format(day, "yyyy-MM-dd"))) {
                                                    onSave(dateId, format(day, "yyyy-MM-dd"));
                                                }
                                            }}
                                        />
                                    );
                                })}
                            </Month>
                        );
                    })}
                </Calendar>
            </Main>
        </DefaultLayout>
    );
};

export default AddGroupCalendar;
